/**
 * External dependencies
 */
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Progress } from '@art-of-hospitality-solutions-llc/ui-kit/components/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/tabs';
import { Database } from 'lucide-react';

/**
 * Internal dependencies
 */
import { Card, CardContent } from '@Components/Card';
import Loader from '@Components/Loader';
import { useCacheStats, useDeleteCacheEntries } from '@Hooks/useCache';
import { formatBytes, formatTtl } from './utils';

export default function CacheStatsCard() {
  const { data: stats, status } = useCacheStats({
    refetchInterval: 30_000,
  });
  const deleteMutation = useDeleteCacheEntries();

  return (
    <Card>
      <CardContent>
        {status === 'pending' ? (
          <Loader size="lg" />
        ) : !stats ? (
          <div className="py-4 text-sm text-zinc-500">No stats available.</div>
        ) : (
          <div className="flex flex-col gap-6">
            <div className="flex items-center gap-3">
              <Database className="size-5 text-zinc-500" />
              <span className="font-semibold">{stats.label}</span>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <div className="text-xs text-zinc-500">Used</div>
                <div className="text-lg font-medium">
                  {formatBytes(stats.bytes)} / {formatBytes(stats.maxBytes)}{' '}
                  <span className="text-sm text-zinc-500">
                    ({stats.usedPercentage}%)
                  </span>
                </div>
                <Progress
                  value={Math.min(stats.usedPercentage, 100)}
                  className="mt-2"
                />
              </div>
              <div>
                <div className="text-xs text-zinc-500">Total entries</div>
                <div className="text-lg font-medium">{stats.count}</div>
              </div>
              <div>
                <div className="text-xs text-zinc-500">
                  TTL (min / avg / max)
                </div>
                <div className="text-lg font-medium">
                  {stats.ttl
                    ? `${formatTtl(stats.ttl.minSeconds)} / ${formatTtl(stats.ttl.avgSeconds)} / ${formatTtl(stats.ttl.maxSeconds)}`
                    : '—'}
                </div>
              </div>
            </div>

            {((stats.byTenant?.length ?? 0) > 0 ||
              stats.byRestaurant?.length > 0 ||
              stats.byResource?.length > 0) && (
              <Tabs defaultValue="tenant">
                <TabsList>
                  <TabsTrigger value="tenant">By tenant</TabsTrigger>
                  <TabsTrigger value="restaurant">By restaurant</TabsTrigger>
                  <TabsTrigger value="resource">By resource</TabsTrigger>
                </TabsList>
                <TabsContent value="tenant">
                  <BreakdownTable
                    headerLabel="Tenant"
                    rows={(stats.byTenant ?? []).map((r) => ({
                      label: r.tenant,
                      count: r.count,
                      bytes: r.bytes,
                    }))}
                    onFlush={(label) =>
                      deleteMutation.mutate({ tenant: label })
                    }
                    flushDisabled={deleteMutation.isPending}
                  />
                </TabsContent>
                <TabsContent value="restaurant">
                  <BreakdownTable
                    headerLabel="Restaurant GUID"
                    rows={stats.byRestaurant.map((r) => ({
                      label: r.restaurantGuid,
                      count: r.count,
                      bytes: r.bytes,
                    }))}
                    onFlush={(label) =>
                      deleteMutation.mutate({ restaurantGuid: label })
                    }
                    flushDisabled={deleteMutation.isPending}
                  />
                </TabsContent>
                <TabsContent value="resource">
                  <BreakdownTable
                    headerLabel="Resource"
                    rows={stats.byResource.map((r) => ({
                      label: r.resource,
                      count: r.count,
                      bytes: r.bytes,
                    }))}
                    onFlush={(label) =>
                      deleteMutation.mutate({ resource: label })
                    }
                    flushDisabled={deleteMutation.isPending}
                  />
                </TabsContent>
              </Tabs>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

type BreakdownRow = { label: string; count: number; bytes: number };

function BreakdownTable({
  headerLabel,
  rows,
  onFlush,
  flushDisabled,
}: {
  headerLabel: string;
  rows: BreakdownRow[];
  onFlush: (label: string) => void;
  flushDisabled: boolean;
}) {
  if (rows.length === 0) {
    return <div className="py-4 text-sm text-zinc-500">No entries.</div>;
  }

  return (
    <div>
      <Table className="w-full">
        <TableHeader>
          <TableRow>
            <TableHead>{headerLabel}</TableHead>
            <TableHead className="text-right">Count</TableHead>
            <TableHead className="text-right">Size</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.label}>
              <TableCell className="font-mono text-xs">{r.label}</TableCell>
              <TableCell className="text-right">{r.count}</TableCell>
              <TableCell className="text-right">
                {formatBytes(r.bytes)}
              </TableCell>
              <TableCell className="text-right">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={flushDisabled}
                  onClick={() => onFlush(r.label)}
                >
                  Flush
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
